import PropTypes from 'prop-types';
import { keyframes } from '@emotion/react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';

import Logo from 'src/components/logo';

// ----------------------------------------------------------------------

const logoPulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }

  16.67% {
    transform: scale(0.9);
    opacity: 0.48;
  }

  33.33% {
    transform: scale(0.9);
    opacity: 0.48;
  }

  50% {
    transform: scale(1);
    opacity: 1;
  }

  66.67% {
    transform: scale(1);
    opacity: 1;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const outerRing = keyframes`
  0% {
    transform: scale(1.6) rotate(270deg);
    opacity: 0.25;
    border-radius: 25%;
  }

  25% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
    border-radius: 25%;
  }

  50% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
    border-radius: 50%;
  }

  75% {
    transform: scale(1.6) rotate(270deg);
    opacity: 1;
    border-radius: 50%;
  }

  100% {
    transform: scale(1.6) rotate(270deg);
    opacity: 0.25;
    border-radius: 25%;
  }
`;

const innerRing = keyframes`
  0% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
    border-radius: 25%;
  }

  25% {
    transform: scale(1.2) rotate(270deg);
    opacity: 0.25;
    border-radius: 25%;
  }

  50% {
    transform: scale(1.2) rotate(270deg);
    opacity: 0.25;
    border-radius: 50%;
  }

  75% {
    transform: scale(1) rotate(0deg);
    opacity: 0.25;
    border-radius: 50%;
  }

  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
    border-radius: 25%;
  }
`;

// ----------------------------------------------------------------------

export default function SplashScreen({ sx, ...other }) {
  return (
    <Box
      sx={(theme) => ({
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.default,
        ...sx,
      })}
      {...other}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            animation: `${logoPulse} 3s ease-in-out infinite`,
            willChange: 'transform, opacity',
            lineHeight: 0,
          }}
        >
          <Logo disabledLink width={40} />
        </Box>

        <Box
          sx={(theme) => ({
            width: 100,
            height: 100,
            position: 'absolute',
            border: `solid 3px ${alpha(theme.palette.primary.dark, 0.24)}`,
            animation: `${outerRing} 3.2s linear infinite`,
            willChange: 'transform, opacity, border-radius',
          })}
        />

        <Box
          sx={(theme) => ({
            width: 120,
            height: 120,
            position: 'absolute',
            border: `solid 8px ${alpha(theme.palette.primary.dark, 0.24)}`,
            animation: `${innerRing} 3.2s linear infinite`,
            willChange: 'transform, opacity, border-radius',
          })}
        />
      </Box>
    </Box>
  );
}

SplashScreen.propTypes = {
  sx: PropTypes.object,
};
